import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { subscribeN4 } from '../lib/n4';
import { Decode } from '../lib/fx';
import { SiteCard } from '../components/sites/SiteCard';
import { NewSiteModal } from '../components/sites/NewSiteModal';
import { type EdgeState, type Site, errText, normalizeSite, pickList, useProjects } from '../components/sites/common';

// Edge-állapot jelvény a fejlécben: mód (teszt / éles) + fut-e; kattintásra az Edge-oldal.
export function EdgeBadge({ edge, err }: { edge: EdgeState | null; err: string }) {
  const state = err ? 'err' : !edge ? 'unk' : !edge.installed ? 'none' : edge.running ? 'run' : 'stop';
  const text = err ? 'nem elérhető' : !edge ? '…' : !edge.installed ? 'nincs telepítve' : edge.running ? 'fut' : 'áll';
  return (
    <Link to="/sites/edge" className={`a-site-edgebadge ${state}`} title={err || 'Edge (Caddy): állapot, import, átállás'}>
      <i className={`a-sitelamp ${state === 'run' ? 'run' : state === 'stop' || state === 'err' ? 'bad' : 'stop'}`} />
      <b>Edge</b>
      {edge?.installed && <span className={`md ${edge.mode === 'prod' ? 'prod' : 'test'}`}>{edge.mode === 'prod' ? 'éles' : 'teszt'}</span>}
      <span>{text}</span><span className="go">›</span>
    </Link>
  );
}

export function Sites() {
  const navigate = useNavigate();
  const projects = useProjects();
  const [sites, setSites] = useState<Site[] | null>(null);
  const [err, setErr] = useState('');
  const [edge, setEdge] = useState<EdgeState | null>(null);
  const [edgeErr, setEdgeErr] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    api<unknown>('/api/n4/sites').then((r) => { setSites(pickList<Record<string, unknown>>(r, 'sites').map(normalizeSite)); setErr(''); }).catch((e) => setErr(errText(e, 'A weboldalak listája')));
    api<EdgeState>('/api/n4/edge').then((r) => { setEdge(r); setEdgeErr(''); }).catch((e) => setEdgeErr(errText(e, 'Edge')));
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30_000);
    // Élő frissítés: site- vagy edge-esemény (indítás, deploy, karbantartás, alkalmazás) → a lista újratölt
    const off = subscribeN4((ev) => { if (ev.type === 'site' || ev.type === 'edge') load(); });
    return () => { clearInterval(t); off(); };
  }, [load]);

  // Egy kártya frissítése: ha a válasz hozta a site-ot, helyben cseréljük; különben újratöltés
  const onChanged = (fresh: Site | null) => { if (fresh) setSites((l) => (l ? l.map((s) => (s.id === fresh.id ? { ...s, ...fresh, live: fresh.live ?? s.live } : s)) : l)); load(); };

  const needle = q.trim().toLowerCase();
  const shown = (sites ?? []).filter((s) => !needle || `${s.name} ${s.title ?? ''} ${s.domains.join(' ')} ${projects.find((p) => p.id === s.project_id)?.name ?? ''}`.toLowerCase().includes(needle));
  const running = (sites ?? []).filter((s) => s.live?.running).length;
  const maint = (sites ?? []).filter((s) => s.maintenance === true || s.maintenance === 1).length;

  return (
    <div className="h-full overflow-auto">
      <div className="a-pg">
        <header className="a-pgh">
          <div className="min-w-0">
            <h1 className="a-title"><Decode text="Weboldalak" duration={800} /></h1>
            <p className="a-sub">Az APM-kezelt oldalak az edge (Caddy) mögött: indítás, napló, fájlok, kiadás, mentés egy helyen.{sites && sites.length > 0 && <> <b>{sites.length}</b> oldal, <b>{running}</b> fut{maint ? <>, <b>{maint}</b> karbantartásban</> : null}.</>}</p>
          </div>
          <div className="a-site-hacts">
            <EdgeBadge edge={edge} err={edgeErr} />
            {sites && sites.length > 3 && <input className="a-input a-site-search" placeholder="Keresés (név, domain, projekt)" value={q} onChange={(e) => setQ(e.target.value)} />}
            <button className="a-btn solid" onClick={() => setShowNew(true)}>+ Új weboldal</button>
          </div>
        </header>

        {err && <div className="a-site-msg bad a-site-block">{err} <button className="a-btn sm" onClick={load}>Újra</button></div>}
        {!sites && !err && <p className="a-sub">betöltés…</p>}
        {sites && sites.length === 0 && (
          <div className="a-panel a-pad a-site-empty">
            <h3>Még nincs weboldal</h3>
            <p>Itt jelennek meg a szerveren futó oldalak: statikus (fájlok a www-mappából), proxy (egy helyi szolgáltatás portja) vagy konténer (APM-recept). Az edge (Caddy) mindegyiknek ad domaint és tanúsítványt.</p>
            <p>A mostani oldalakat egy lépésben áthozhatod: az Edge-oldalon az <b>Import az OpenResty-ből</b> a meglévő vhostokból site-sorokat készít (a régi bejárat közben változatlanul fut).</p>
            <div className="flex flex-wrap gap-2"><Link className="a-btn pri" to="/sites/edge">Importálás az OpenResty-ből ›</Link><button className="a-btn ghost" onClick={() => setShowNew(true)}>+ Új weboldal kézzel</button></div>
          </div>
        )}
        {sites && sites.length > 0 && (
          <div className="a-sites-list a-stagger">
            {shown.map((s, i) => <SiteCard key={s.id} site={s} projects={projects} index={i} onChanged={onChanged} />)}
            {needle && shown.length === 0 && <p className="a-sub">Nincs találat erre: „{q}”.</p>}
          </div>
        )}
      </div>
      {showNew && <NewSiteModal projects={projects} onClose={() => setShowNew(false)} onCreated={(s) => { setShowNew(false); load(); if (s) navigate(`/sites/${s.id}?tab=settings`); }} />}
    </div>
  );
}
